import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Loader2, Wallet, CreditCard, ExternalLink, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDERS = [
  {
    id: "stripe",
    title: "Stripe",
    icon: CreditCard,
    color: "text-violet-400",
    accent: "bg-violet-500",
    docsUrl: "https://dashboard.stripe.com/apikeys",
    description: "Tarjetas internacionales, Apple Pay, Google Pay. Ideal para clientes globales (USD/EUR).",
    fields: [
      { key: "secret_key", label: "Secret Key (sk_live_… / sk_test_…)", placeholder: "sk_live_…", type: "password" },
      { key: "publishable_key", label: "Publishable Key (pk_…)", placeholder: "pk_live_…", type: "text" },
      { key: "webhook_secret", label: "Webhook signing secret (whsec_…)", placeholder: "whsec_…", type: "password" },
    ],
    notes: "Configurá el webhook a https://TU-DOMINIO/api/webhook/stripe escuchando 'checkout.session.completed'.",
  },
  {
    id: "mercadopago",
    title: "Mercado Pago",
    icon: Wallet,
    color: "text-sky-400",
    accent: "bg-sky-500",
    docsUrl: "https://www.mercadopago.com.ar/developers/panel/app",
    description: "Tarjeta + efectivo (Rapipago, OXXO, Pix). Ideal para LATAM.",
    fields: [
      { key: "access_token", label: "Access Token (APP_USR-… o TEST-…)", placeholder: "APP_USR-…", type: "password" },
      { key: "public_key", label: "Public Key", placeholder: "APP_USR-…", type: "text" },
      { key: "webhook_secret", label: "Webhook Secret (HMAC)", placeholder: "Tu firma de webhook", type: "password" },
    ],
    notes: "Configurá el webhook a https://TU-DOMINIO/api/webhook/mercadopago escuchando 'payment' y 'payment.updated'.",
  },
  {
    id: "paypal",
    title: "PayPal",
    icon: ShieldCheck,
    color: "text-amber-400",
    accent: "bg-amber-500",
    docsUrl: "https://developer.paypal.com/dashboard/applications",
    description: "Pagos con cuenta PayPal o tarjeta. Cobra en USD principalmente.",
    fields: [
      { key: "client_id", label: "Client ID", placeholder: "AaBbCc…", type: "text" },
      { key: "client_secret", label: "Client Secret", placeholder: "EOe…", type: "password" },
      { key: "mode", label: "Modo (sandbox / live)", placeholder: "sandbox", type: "text" },
      { key: "webhook_id", label: "Webhook ID", placeholder: "5KU…", type: "text" },
    ],
    notes: "PayPal aún no está integrado en el flujo de checkout — guardar las keys lo prepara para cuando lo activemos.",
  },
];

const EMPTY = {
  stripe: { enabled: false, secret_key: "", publishable_key: "", webhook_secret: "" },
  mercadopago: { enabled: false, access_token: "", public_key: "", webhook_secret: "" },
  paypal: { enabled: false, client_id: "", client_secret: "", mode: "sandbox", webhook_id: "" },
};

export default function AdminPaymentProviders() {
  const { getAdminAxios } = useOutletContext();
  const adminAxios = getAdminAxios();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    adminAxios.get(`${API}/admin/payment-providers`)
      .then(({ data }) => setData({ ...EMPTY, ...data }))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleField = (provider, key, value) => {
    setData((prev) => ({ ...prev, [provider]: { ...prev[provider], [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      await adminAxios.put(`${API}/admin/payment-providers`, data);
      setSaved(true);
      // re-fetch to display masked values
      const { data: fresh } = await adminAxios.get(`${API}/admin/payment-providers`);
      setData({ ...EMPTY, ...fresh });
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-8"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>;
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="admin-payment-providers">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
          <Wallet className="w-6 h-6 text-amber-400" /> Pasarelas de pago
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Configurá las API keys de cada pasarela. El toggle activa o desactiva cada método en el checkout de los clientes.
        </p>
      </div>

      {error && (
        <Card className="mb-4 border-red-700/40 bg-red-900/20"><CardContent className="p-3 flex items-center gap-2 text-sm text-red-300">
          <AlertTriangle className="w-4 h-4" /> {error}
        </CardContent></Card>
      )}

      <div className="space-y-4">
        {PROVIDERS.map((p) => {
          const Icon = p.icon;
          const cfg = data[p.id] || {};
          return (
            <Card key={p.id} className="bg-[#13112b] border-indigo-900/30" data-testid={`provider-${p.id}-card`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg ${p.accent}/10 flex items-center justify-center border border-white/10`}>
                      <Icon className={`w-5 h-5 ${p.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base text-white flex items-center gap-2">
                        {p.title}
                        <a href={p.docsUrl} target="_blank" rel="noreferrer" className="text-xs text-amber-400 hover:underline inline-flex items-center gap-1">
                          docs <ExternalLink className="w-3 h-3" />
                        </a>
                      </CardTitle>
                      <CardDescription className="text-slate-400 text-xs mt-0.5">{p.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400">Activo</span>
                    <Switch
                      checked={!!cfg.enabled}
                      onCheckedChange={(v) => handleField(p.id, "enabled", v)}
                      data-testid={`provider-${p.id}-toggle`}
                    />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  {p.fields.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-slate-300 text-xs">{f.label}</Label>
                      <Input
                        type={f.type === "password" ? "text" : f.type}
                        value={cfg[f.key] || ""}
                        onChange={(e) => handleField(p.id, f.key, e.target.value)}
                        placeholder={f.placeholder}
                        autoComplete="off"
                        spellCheck={false}
                        className="bg-white/5 border-white/10 text-white font-mono text-xs"
                        data-testid={`provider-${p.id}-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
                {p.notes && (
                  <p className="text-xs text-slate-500 border-l-2 border-slate-700 pl-2">{p.notes}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-600 text-black"
          data-testid="save-payment-providers"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "Guardando…" : "Guardar configuración"}
        </Button>
        {saved && <span className="text-sm text-emerald-400 font-medium" data-testid="providers-save-success">Guardado ✓</span>}
        <p className="text-xs text-slate-500 ml-auto">
          🔒 Las keys se enmascaran al volver a leerlas. Si dejás el campo con bullets (•••), se mantiene la key existente.
        </p>
      </div>
    </div>
  );
}
